import React from 'react'
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
    Radio,
    Button,
    Box
} from '@material-ui/core'
import { makeStyles, createMuiTheme, ThemeProvider } from '@material-ui/core/styles'
import { ColorEastBay, AtlasGreyLight } from 'assets/styles/variables'
import i18n from 'common/util/internationalization'
import { truncateString } from 'common/util/functions'
import { MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH_IN_SIDEBAR } from 'common/constants/constants'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'

interface MinutesTemplateListComponentProps {
    templateOptions?: any[]
    templateOption?: any
    handleLoadMore?: () => void
    isLoading?: boolean
    hasMoreTemplates?: boolean
    handleGetTemplatePreview?: (templateId: string) => void
    handlePreviewButton?: (templateId: string) => void
    previewSelection?: any
    setPreviewSelection?: (template: any) => void
    showPreview?: boolean // Show preview button for iPad
}

const useStyles = makeStyles((theme) => ({
    tableContainer: {
        padding: '1px 1px 20px 1px',
        gap: '24px',
        alignItems: 'center',
        overflowX: 'auto',
        width: '100%',
        [theme.breakpoints.down('md')]: {
            overflowX: 'auto',
            background: 'white',
            width: '100%',
            // Apply paddingBottom only for iPad-sized screens (between 768px and 1024px)
            [theme.breakpoints.between('sm', 'md')]: {
                paddingBottom: '30px'
            }
        },
        '&::-webkit-scrollbar': {
            width: '6px'
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#acb1b8',
            borderRadius: '4px'
        },
        '&::-webkit-scrollbar-track': {
            background: 'transparent',
            borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: '#acb1b8'
        }
    },
    tableSpacing: {
        borderSpacing: '0 10px',
        tableLayout: 'fixed',
        maxWidth: '100%'
    },
    tableRow: {
        '& > *': {
            borderBottom: 'none',
            padding: '8px'
        },
        borderRadius: '10px',
        outline: `1px solid ${AtlasGreyLight}`,
        textAlign: 'left'
    },
    selectedTableRow: {
        '& > *': {
            borderBottom: 'none',
            padding: '8px'
        },
        borderRadius: '10px',
        outline: '1px solid #455D82',
        background: '#FFF',
        textAlign: 'left'
    },
    tableRowSpacer: {
        height: '10px'
    },
    radioContainer: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#fff'
    },
    templateContent: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        wordBreak: 'break-word',
        justifyContent: 'space-between',
        width: '100%'
    },
    labelText: {
        fontFamily: 'Source Sans Pro',
        fontWeight: 600,
        fontSize: '16px',
        color: '#455D82'
    },
    defaultTag: {
        fontSize: '12px',
        backgroundColor: '#f0f0f0',
        borderRadius: '12px',
        padding: '2px 8px',
        color: '#666',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        alignSelf: 'center',
        marginLeft: 'auto'
    },
    templateCardPreviewLink: {
        color: '#455D82',
        fontWeight: 600,
        fontSize: '14px',
        cursor: 'pointer',
        fontFamily: 'Source Sans Pro',
        marginLeft: '5px',
        marginRight: '20px'
    },
    showMoreBtn: {
        textTransform: 'none',
        color: `${ColorEastBay}`,
        fontFamily: 'Source Sans Pro',
        fontWeight: 600,
        lineHeight: '20px',
        border: `1px solid ${ColorEastBay}`,
        padding: '8px 12px 8px 12px',
        maxWidth: '169px',
        display: 'block',
        margin: 'auto',
        '&:focus': {
            outline: 'none',
            boxShadow: `0px 0px 0px 1px #ffffff, 0px 0px 0px 3px ${ColorEastBay}`,
            borderRadius: '2px'
        }
    },
    templateTitle: {
        fontSize: '16px',
        fontFamily: 'Source Sans Pro',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '20px',
        color: '#1E1E1E'
    },
    templateSubtitle: {
        fontSize: '14px',
        fontFamily: 'Source Sans Pro',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '18px',
        color: '#6B7280'
    },
    templateCardContent: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
    }
}))

const MinutesTemplateListComponent: React.FC<MinutesTemplateListComponentProps> = ({
    templateOptions,
    templateOption,
    handleLoadMore,
    isLoading,
    hasMoreTemplates,
    handleGetTemplatePreview,
    handlePreviewButton,
    previewSelection,
    setPreviewSelection,
    showPreview = false
}) => {
    // Determine which template should be selected
    // let selectedTemplateOption = templateOption
    // if (Object.keys(templateOption || {}).length === 0 && templateOptions && templateOptions.length > 0) {
    //     selectedTemplateOption = systemTemplate // first template
    // }
    const isAdmin = true // or derive this value from props or state
    const classes = useStyles({ isAdmin })
    const theme = createMuiTheme({
        overrides: {
            MuiRadio: {
                root: {
                    color: '#2F3B4D',
                    padding: '16px'
                },
                colorSecondary: {
                    '&.Mui-checked': {
                        color: '#2F3B4D'
                    },
                    '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    },
                    '& .MuiSvgIcon-root': {
                        color: '#2F3B4D'
                    },
                    '&.Mui-checked .MuiSvgIcon-root': {
                        color: '#2F3B4D'
                    }
                }
            }
        }
    })
    return (
        <TableContainer className={classes.tableContainer}>
            <Table className={classes.tableSpacing}>
                <TableBody>
                    <React.Fragment key={0}>
                        {templateOptions &&
                            templateOptions?.length !== 0 &&
                            templateOptions.map((options, index) => (
                                <React.Fragment key={index}>
                                    <TableRow
                                        className={
                                            (previewSelection?.id || templateOption?.id) ===
                                            options.id
                                                ? classes.selectedTableRow
                                                : classes.tableRow
                                        }
                                        key={index}
                                        onClick={() => {
                                            setPreviewSelection?.(options)
                                            // Only call preview if there's no separate preview button handler (i.e., not iPad)
                                            if (!handlePreviewButton) {
                                                handleGetTemplatePreview?.(options.id)
                                            }
                                        }}
                                        style={{ cursor: 'pointer' }}>
                                        <TableCell
                                            colSpan={2}
                                            padding="default"
                                            align="left"
                                            role="cell">
                                            <div className={classes.radioContainer}>
                                                <ThemeProvider theme={theme}>
                                                    <Radio
                                                        checked={
                                                            (previewSelection?.id ||
                                                                templateOption?.id) === options.id
                                                        }
                                                        value={options.id}
                                                        onChange={() => {
                                                            setPreviewSelection?.(options)
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (
                                                                e.key === ' ' ||
                                                                e.key === 'Enter'
                                                            ) {
                                                                e.preventDefault()
                                                                setPreviewSelection?.(options)
                                                                handleGetTemplatePreview?.(
                                                                    options.id
                                                                )
                                                            }
                                                        }}
                                                        name={options.title}
                                                        tabIndex={0}
                                                        inputProps={{
                                                            'aria-label': 'Select Export Option'
                                                        }}
                                                    />
                                                </ThemeProvider>
                                                <div className={classes.templateContent}>
                                                    <Box className={classes.templateCardContent}>
                                                        {options.title && (
                                                            <AtlasTooltip
                                                                title={
                                                                    options.site === null
                                                                        ? i18n.t(
                                                                              'MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE'
                                                                          )
                                                                        : options.title?.length >
                                                                          MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH_IN_SIDEBAR
                                                                        ? options.title
                                                                        : ''
                                                                }
                                                                interactive
                                                                placement="top">
                                                                <Typography
                                                                    className={
                                                                        classes.templateTitle
                                                                    }>
                                                                    {options.site === null
                                                                        ? i18n.t(
                                                                              'MINUTES_TEMPLATES_DILIGENT_TEMPLATE_TITLE'
                                                                          )
                                                                        : truncateString(
                                                                              options.title,
                                                                              MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH_IN_SIDEBAR
                                                                          )}
                                                                </Typography>
                                                            </AtlasTooltip>
                                                        )}
                                                        {options.description && (
                                                            <AtlasTooltip
                                                                title={
                                                                    options.site === null
                                                                        ? i18n.t(
                                                                              'MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION'
                                                                          )
                                                                        : options.description
                                                                              ?.length >
                                                                          MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH_IN_SIDEBAR
                                                                        ? options.description
                                                                        : ''
                                                                }
                                                                interactive
                                                                placement="top">
                                                                <Typography
                                                                    className={
                                                                        classes.templateSubtitle
                                                                    }>
                                                                    {options.site === null
                                                                        ? i18n.t(
                                                                              'MINUTES_TEMPLATES_DILIGENT_TEMPLATE_DESCRIPTION'
                                                                          )
                                                                        : truncateString(
                                                                              options.description,
                                                                              MAX_TEMPLATE_TITLE_DESCTIPTION_LENGTH_IN_SIDEBAR
                                                                          )}
                                                                </Typography>
                                                            </AtlasTooltip>
                                                        )}
                                                    </Box>
                                                    {options.isDefault && (
                                                        <span className={classes.defaultTag}>
                                                            {i18n.t(
                                                                'MINUTES_TEMPLATES_DEFAULT_TEMPLATE'
                                                            )}
                                                        </span>
                                                    )}
                                                </div>

                                                {showPreview && (
                                                    <Typography
                                                        className={classes.templateCardPreviewLink}
                                                        tabIndex={0}
                                                        role="button"
                                                        data-analytics="MM-Template-Export-Preview"
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            if (handlePreviewButton) {
                                                                handlePreviewButton(options.id)
                                                            } else {
                                                                handleGetTemplatePreview?.(
                                                                    options.id
                                                                )
                                                            }
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (
                                                                e.key === ' ' ||
                                                                e.key === 'Enter'
                                                            ) {
                                                                e.preventDefault()
                                                                e.stopPropagation()
                                                                if (handlePreviewButton) {
                                                                    handlePreviewButton(options.id)
                                                                } else {
                                                                    handleGetTemplatePreview?.(
                                                                        options.id
                                                                    )
                                                                }
                                                            }
                                                        }}>
                                                        {i18n.t('TEMPLATE_CARD_PREVIEW')}
                                                    </Typography>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                    <tr className={classes.tableRowSpacer}></tr>
                                </React.Fragment>
                            ))}
                    </React.Fragment>
                </TableBody>
            </Table>
            {hasMoreTemplates && (
                <Button
                    disableFocusRipple
                    disableRipple
                    onClick={handleLoadMore}
                    disabled={isLoading}
                    data-testid="ShowMore_Btn"
                    aria-label={i18n.t('MINUTES_SHOW_MORE_LABEL') as string}
                    data-analytics="MM-ShowMoreTemplates"
                    className={classes.showMoreBtn}>
                    {i18n.t('MINUTES_SHOW_MORE_LABEL')}
                </Button>
            )}
        </TableContainer>
    )
}

export default MinutesTemplateListComponent
